import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowLeft, ExternalLink, Images, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import MediaCarousel from '../components/MediaCarousel';
import { useLocalize } from '../i18n/useLocalize';
import PackageCTA from '../components/PackageCTA';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Resolve relative paths (e.g. /api/uploads/foo.jpg) to absolute URLs.
const absolutize = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (/^https?:\/\//i.test(url) || url.startsWith('blob:') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

const HotDestinations = () => {
  const [destinations, setDestinations] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [linkedPackages, setLinkedPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollPosRef = useRef(0);
  const { t } = useTranslation();
  const localize = useLocalize();
  const { slug } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const [curatedRes, hotRes] = await Promise.all([
          axios.get(`${API}/curated-destinations`),
          axios.get(`${API}/destinations`).catch(() => ({ data: [] })),
        ]);

        const curated = (curatedRes.data || []).map((d) => ({
          ...d,
          _source: 'curated',
          _media: (d.media && d.media.length > 0) ? d.media : (d.image ? [d.image] : []),
        }));

        // Normalize legacy "Hot Destinations" entries into the curated card shape
        const hot = (hotRes.data || []).map((d) => {
          const facts = [];
          if (d.discount) facts.push({ label: 'Deal', value: d.discount });
          if (d.valid_until) facts.push({ label: 'Valid Until', value: d.valid_until });
          const media = (d.media && d.media.length > 0) ? d.media : (d.image_url ? [d.image_url] : []);
          return {
            id: d.id,
            title: d.name,
            image: d.image_url,
            vibe: d.description,
            description: d.description,
            facts,
            linked_package_ids: [],
            _source: 'hot',
            _media: media,
          };
        });

        setDestinations([...curated, ...hot]);
      } catch (error) {
        console.error('Error fetching destinations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDestinations();
  }, []);

  // Deep-link support: when arriving at /destinations/:slug (canonical SEO URL)
  // OR /destinations?destination=<id> (legacy share URL), open that destination's
  // detail view automatically.
  useEffect(() => {
    if (loading || destinations.length === 0 || selectedDestination) return;
    let dest = null;
    if (slug) {
      dest = destinations.find((d) => d.slug === slug);
    } else {
      const params = new URLSearchParams(window.location.search);
      const targetId = params.get('destination');
      if (targetId) dest = destinations.find((d) => d.id === targetId);
    }
    if (dest) {
      selectDestination(dest);
      // Strip legacy ?destination=<id> query string (canonical /destinations/:slug stays)
      if (!slug && window.location.search) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, destinations, slug]);

  const selectDestination = async (dest) => {
    scrollPosRef.current = window.scrollY;
    setSelectedDestination(dest);
    window.scrollTo(0, 0);

    // Update URL to canonical /destinations/:slug for curated destinations so
    // refresh / share / back-button all work naturally. Use replace=true when
    // the user already arrived on a slug URL to avoid stacking history.
    if (dest._source === 'curated' && dest.slug) {
      const target = `/destinations/${dest.slug}`;
      if (window.location.pathname !== target) {
        navigate(target, { replace: !!slug });
      }
    }

    // Only curated destinations support linked partner packages.
    if (dest._source !== 'curated') {
      setLinkedPackages([]);
      return;
    }

    try {
      const response = await axios.get(`${API}/curated-destinations/${dest.id}`);
      setLinkedPackages(response.data.packages || []);
    } catch (error) {
      setLinkedPackages([]);
    }
  };

  const goBack = () => {
    setSelectedDestination(null);
    setLinkedPackages([]);
    // Return to the list URL; preserves natural back-button behavior.
    if (window.location.pathname !== '/destinations') {
      navigate('/destinations', { replace: false });
    }
    setTimeout(() => {
      window.scrollTo(0, scrollPosRef.current);
    }, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-maroon text-xl">Loading destinations...</p>
      </div>
    );
  }

  // ===== DETAIL VIEW =====
  if (selectedDestination) {
    const dest = localize(selectedDestination, ['title', 'vibe', 'description', 'facts']);
    const filtered = linkedPackages;

    return (
      <div data-testid="destination-detail-view">
        {/* Sticky Top Bar with Breadcrumb */}
        <div className="sticky top-0 z-40 bg-cream/95 backdrop-blur-sm border-b border-maroon/15">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
            <nav className="flex items-center text-sm" aria-label="Breadcrumb" data-testid="breadcrumb-nav">
              <a href="/" className="text-maroon/50 hover:text-terracotta transition-colors">{t('destinations.breadcrumbHome')}</a>
              <span className="mx-2 text-maroon/30">&gt;</span>
              <button onClick={goBack} className="text-maroon/50 hover:text-terracotta transition-colors">{t('destinations.breadcrumbDestinations')}</button>
              <span className="mx-2 text-maroon/30">&gt;</span>
              <span className="text-maroon font-semibold">{dest.title}</span>
            </nav>
            <button
              onClick={goBack}
              className="inline-flex items-center gap-2 text-maroon hover:text-terracotta font-semibold text-sm transition-colors"
              data-testid="back-to-destinations-btn"
            >
              <ArrowLeft size={18} />
              <span className="hidden sm:inline">{t('destinations.back')}</span>
              <span className="sm:hidden">{t('destinations.breadcrumbDestinations')}</span>
            </button>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative bg-black">
          {selectedDestination._media && selectedDestination._media.length > 1 ? (
            <div className="max-w-7xl mx-auto">
              <MediaCarousel
                media={selectedDestination._media}
                alt={dest.title}
                aspectClassName="aspect-[16/9] sm:aspect-[21/9]"
              />
            </div>
          ) : (
            <div className="relative h-[400px] overflow-hidden">
              <img
                src={absolutize(selectedDestination.image)}
                alt={dest.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
            </div>
          )}
        </section>

        {/* Title + Facts (below hero so it doesn't fight with carousel controls) */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="flex flex-wrap gap-2 mb-4">
            {(dest.facts || []).map((fact, idx) => (
              <span
                key={idx}
                className="bg-sand/60 text-maroon text-xs font-medium px-3 py-1.5 rounded-sm border border-maroon/15"
              >
                {fact.label}: <span className="font-bold">{fact.value}</span>
              </span>
            ))}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-maroon">
            {dest.title}
          </h1>
          <button
            type="button"
            onClick={async () => {
              const origin = (typeof window !== 'undefined' && window.location?.origin) || BACKEND_URL;
              const url = `${origin}/api/share/destination/${selectedDestination.id}`;
              const data = { title: dest.title, text: dest.vibe || '', url };
              try {
                if (typeof navigator !== 'undefined' && navigator.share) {
                  await navigator.share(data);
                  return;
                }
              } catch { /* user cancelled */ }
              try {
                await navigator.clipboard.writeText(url);
                toast.success('Share link copied — paste it anywhere for a rich preview.');
              } catch {
                toast.error('Could not copy share link');
              }
            }}
            className="mt-4 inline-flex items-center gap-2 border border-maroon/30 text-maroon hover:bg-maroon/5 font-semibold text-sm uppercase tracking-wide rounded-sm px-4 py-2 transition-colors"
            data-testid="destination-share-btn"
            title="Share with rich preview"
          >
            <Share2 size={14} />
            Share
          </button>
        </section>

        {/* Description */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <p className="text-lg text-maroon/70 leading-relaxed max-w-4xl">
            {dest.description}
          </p>
        </section>

        {/* Filtered Partner Packages */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-maroon mb-2">
            {t('destinations.packagesFor', { title: dest.title })}
          </h2>
          <p className="text-maroon/60 mb-8">
            {t('destinations.pageSubtitle')}
          </p>

          {filtered.length === 0 ? (
            <div className="bg-sand/30 border border-maroon/10 rounded-sm p-10 text-center">
              <p className="text-maroon/60 mb-2">{t('destinations.noPackages')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((rawPkg) => {
                const pkg = localize(rawPkg, ['title', 'destination', 'duration', 'tags']);
                return (
                <div
                  key={pkg.id}
                  className="card group hover:border-terracotta/40 flex flex-col"
                  data-testid={`partner-pkg-card-${pkg.id}`}
                >
                  {pkg.image && (
                    <div className="aspect-[4/3] overflow-hidden rounded-sm border border-maroon/10 mb-4">
                      <img
                        src={absolutize(pkg.image)}
                        alt={pkg.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="flex-1 flex flex-col">
                    {/* Supplier Name */}
                    <p className="text-xs font-semibold text-terracotta uppercase tracking-wider mb-1">{pkg.supplier}</p>

                    {/* Package Title */}
                    <h3 className="font-heading font-bold text-xl text-maroon mb-3">{pkg.title}</h3>

                    {/* Price */}
                    <div className="mt-auto pt-4 border-t border-maroon/10 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-maroon/40">From</p>
                        <p className="text-2xl font-heading font-bold text-maroon">{pkg.price}</p>
                        <p className="text-xs text-maroon/40">per person</p>
                      </div>

                      {/* View Availability Button */}
                      <PackageCTA pkg={pkg} testIdPrefix="dest-cta" />
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    );
  }

  // ===== GRID VIEW =====
  return (
    <div data-testid="hot-destinations-page">
      {/* Hero */}
      <section className="py-16 bg-ocean/5 border-b border-maroon/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-maroon mb-4">
            {t('destinations.pageTitle')}
          </h1>
          <p className="text-lg text-maroon/60 max-w-2xl mx-auto">
            {t('destinations.pageSubtitle')}
          </p>
        </div>
      </section>

      {/* Curated Grid — matches the Home page destination card style */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {destinations.map((rawDest) => {
            const dest = localize(rawDest, ['title', 'vibe', 'description', 'facts']);
            const media = (rawDest._media && rawDest._media.length > 0)
              ? rawDest._media
              : (dest.image ? [dest.image] : []);
            return (
              <div
                key={dest.id}
                onClick={() => selectDestination(rawDest)}
                className="card group cursor-pointer border hover:border-terracotta transition-colors"
                data-testid={`destination-card-${dest.id}`}
              >
                <div className="relative">
                  <MediaCarousel media={media} alt={dest.title} />
                  {rawDest._media && rawDest._media.length > 1 && (
                    <div
                      className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 bg-black/55 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-sm pointer-events-none"
                      data-testid={`media-count-${dest.id}`}
                    >
                      <Images size={12} />
                      <span>{rawDest._media.length}</span>
                    </div>
                  )}
                </div>
                <h3 className="font-heading font-bold text-xl text-maroon mt-4 mb-2">
                  {dest.title}
                </h3>
                <p className="text-maroon/60 text-sm mb-3 line-clamp-2">{dest.vibe}</p>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-1 min-w-0">
                    {(dest.facts || []).slice(0, 1).map((fact, i) => (
                      <span key={i} className="text-xs text-maroon/50 truncate">
                        {fact.label}: <span className="font-semibold text-maroon/70">{fact.value}</span>
                      </span>
                    ))}
                  </div>
                  <span className="text-terracotta font-semibold flex items-center gap-1 text-sm shrink-0">
                    <span>{t('home.destinations.explore')}</span>
                    <ArrowRight size={16} />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default HotDestinations;
